import type { CSSProperties, ReactNode } from 'react';
import type { DrawingBox, DrawnKey, FixedKey, HandsetLayout } from '@arcnet/contracts';
import { Icon, type IconName } from '../../components/ui/Icon.js';

/**
 * A desk phone, drawn from its plug-in's layout (docs/29 decision 14): the template editor's
 * main view, modelled on Yealink's Configuration Generator, where the phone is clicked key by key.
 *
 * Our own outline, never a vendor photo. Every part is a box the plug-in measured on the maker's
 * straight-on photo, so each key is where it is on the phone and the shape it is, and a smaller
 * phone is drawn smaller. It can show our labels on its screen, turn its pages, follow the theme
 * and be used from a keyboard. Keys that can be set are buttons; the keypad, headset, speaker
 * and volume are drawn for where they are and nothing more.
 *
 * Line keys are buttons beside the screen (layout.face 'keys'), or on the screen itself
 * ('touch'). The drawing holds no state: which page is showing, which key is selected and what
 * each key says all come from the editor.
 */

/** What the drawing shows for one key. */
export interface FaceKey {
  /** Beside the key on the screen, on a touch key, or on the soft key row. Empty for nothing. */
  label: string;
  /** Set by the template, rather than empty or as the phone has it. */
  assigned: boolean;
  /** Its name for a screen reader and the tooltip: "Line key 7: Busy lamp, Sipho". */
  describe: string;
}

export const FIXED_KEY_NAMES: Readonly<Record<FixedKey, string>> = {
  soft1: 'Soft key 1', soft2: 'Soft key 2', soft3: 'Soft key 3', soft4: 'Soft key 4',
  up: 'Up', down: 'Down', left: 'Left', right: 'Right', ok: 'OK', cancel: 'Cancel',
  hold: 'Hold', mute: 'Mute', transfer: 'Transfer', redial: 'Redial', message: 'Messages',
};

/** What each key shows on the phone, where it is not a word. */
const GLYPH: Readonly<Partial<Record<FixedKey | DrawnKey, IconName>>> = {
  up: 'chevronUp', down: 'chevronDown', left: 'chevronLeft', right: 'chevronRight', cancel: 'close',
  hold: 'hold', mute: 'mute', transfer: 'transfer', redial: 'redial', message: 'mail',
  headset: 'headset', speaker: 'speaker',
};

const PAD: ReadonlyArray<readonly [string, string]> = [
  ['1', ''], ['2', 'ABC'], ['3', 'DEF'], ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
  ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'], ['*', ''], ['0', ''], ['#', ''],
];

/** Where the navigation keys sit in their ring: centre across, centre down, and size, as shares. */
const RING: ReadonlyArray<readonly [FixedKey, number, number, number]> = [
  ['up', 0.5, 0.15, 0.26], ['down', 0.5, 0.85, 0.26], ['left', 0.15, 0.5, 0.26],
  ['right', 0.85, 0.5, 0.26], ['ok', 0.5, 0.5, 0.36],
];

/** Every phone is drawn to this scale, until the page is too narrow for it. */
const PX_PER_MM = 3;

/** Text as a share of its row: a keypad digit, a label beside the screen, a label on a touch key. */
const DIGIT_SHARE = 0.6;
const LABEL_SHARE = 0.72;
const TOUCH_SHARE = 0.5;

/** A length in the drawing's units, which are a thousandth of its width, with a floor for text. */
const text = (units: number, floor = 8) => `max(${floor}px, ${(units / 10).toFixed(2)}cqw)`;

/**
 * How the line keys page. Up to the keys a page shows, one page holds them all; past them, a
 * page holds one fewer and its last key (bottom right, or More) turns the pages (Yealink V86
 * administrator guide).
 */
export function linePaging(layout: HandsetLayout, showPages: boolean): {
  slots: number; perPage: number; pages: number; paged: boolean;
} {
  const { sides, per_side: perSide, total } = layout.line_keys;
  const slots = perSide * (sides === 'both' ? 2 : 1);
  const paged = showPages && total > slots;
  return paged
    ? { slots, perPage: slots - 1, pages: layout.line_keys.pages, paged }
    : { slots, perPage: slots, pages: 1, paged };
}

export function HandsetFace({
  layout, model, page, showPages, line, fixed, selected, onSelect, onPage,
}: {
  layout: HandsetLayout;
  model: string;
  /** From 1. */
  page: number;
  /** Whether the line keys are paged: some key past the first page is in use. */
  showPages: boolean;
  line: (position: number) => FaceKey;
  fixed: (key: FixedKey) => FaceKey;
  /** 'line:7' or 'fixed:12'. */
  selected: string | null;
  onSelect: (slot: string) => void;
  onPage: (page: number) => void;
}) {
  const d = layout.drawing;
  const touch = layout.face === 'touch';
  const { sides, per_side: perSide, total } = layout.line_keys;
  const { slots, perPage, pages, paged } = linePaging(layout, showPages);
  const first = (page - 1) * perPage + 1;
  const next = (page % pages) + 1;
  const has = (k: FixedKey) => layout.fixed.find((f) => f.key === k);

  /** A box as a place on the drawing. */
  const at = ([x, y, w, h]: DrawingBox): CSSProperties => ({
    left: `${x / 10}%`, top: `${(y / d.height) * 100}%`,
    width: `${w / 10}%`, height: `${(h / d.height) * 100}%`,
  });

  /** Physical slot i: the left column top to bottom, then the right. Paged, the last turns. */
  const slotAt = (i: number): number | 'page' | null => {
    if (paged && i === slots - 1) return 'page';
    const n = first + i;
    return n <= total ? n : null;
  };
  const leftCount = sides === 'both' ? perSide : 0;
  const sideOf = (i: number) => (i < leftCount ? 'left' : 'right') as 'left' | 'right';
  const rowOf = (i: number) => (i < leftCount ? i : i - leftCount);
  const keyBox = (i: number): DrawingBox | null => {
    const run = d.line[sideOf(i)];
    if (!run) return null;
    const [x, y, w, h] = run.first;
    return [x, y + rowOf(i) * run.step, w, h];
  };
  const labelBox = (i: number): DrawingBox | null => {
    const cells = d.labels?.[sideOf(i)];
    if (!d.labels || !cells) return null;
    const [top, height, step] = d.labels.rows;
    return [cells[0], top + rowOf(i) * step, cells[1], height];
  };
  const physical = Array.from({ length: slots }, (_, i) => i);
  const turnLabel = touch ? 'More' : `${page}/${pages}`;

  const setKey = (slot: string, face: FaceKey, className: string, box: DrawingBox,
    body: ReactNode, extra?: { side?: string; size?: string }) => (
    <button key={slot} type="button" className={className} data-side={extra?.side}
      data-assigned={face.assigned} data-selected={selected === slot}
      aria-label={face.describe} aria-pressed={selected === slot} title={face.describe}
      style={{ ...at(box), fontSize: extra?.size }} onClick={() => onSelect(slot)}>
      {body}
    </button>
  );

  // ------------------------------------------------------------------------- line keys
  const lineKeys = physical.map((i) => {
    const box = keyBox(i);
    if (!box) return null;
    const n = slotAt(i);
    const side = sideOf(i);
    const size = text(box[3] * (touch ? TOUCH_SHARE : LABEL_SHARE));
    const cls = touch ? 'arc-phone-tkey' : 'arc-phone-key arc-phone-line';
    if (n === 'page') {
      return (
        <button key={`page${i}`} type="button" className={cls} data-side={side} data-assigned="true"
          aria-label={`Turn to page ${next} of ${pages}`} title={`Page ${next}`}
          style={{ ...at(box), fontSize: size }} onClick={() => onPage(next)}>
          {touch ? <span className="arc-phone-clip">More</span> : <span className="arc-phone-led" />}
        </button>
      );
    }
    if (n === null) {
      return <span key={`none${i}`} className={cls} data-side={side} data-empty="true" style={at(box)} aria-hidden="true" />;
    }
    const k = line(n);
    return setKey(`line:${n}`, k, cls, box, touch
      ? <><span className="arc-phone-clip">{k.label}</span><span className="arc-phone-led" /></>
      : <span className="arc-phone-led" />, { side, size });
  });

  // The labels beside a keys face's screen edge, and the printed lines that tie each to its key.
  const cells = touch ? [] : physical.map((i) => {
    const box = labelBox(i);
    if (!box) return null;
    const n = slotAt(i);
    const words = n === 'page' ? turnLabel : n === null ? '' : line(n).label;
    return (
      <span key={`cell${i}`} className="arc-phone-text arc-phone-cell" data-side={sideOf(i)}
        data-filled={words !== ''} data-selected={typeof n === 'number' && selected === `line:${n}`}
        style={{ ...at(box), fontSize: text(box[3] * LABEL_SHARE) }} aria-hidden="true">
        <span className="arc-phone-clip">{words}</span>
      </span>
    );
  });
  const [sx, , sw] = d.screen;
  const wires = touch ? [] : physical.map((i) => {
    const key = keyBox(i);
    const cell = labelBox(i);
    if (!key || !cell) return null;
    const left = sideOf(i) === 'left';
    const from = left ? key[0] + key[2] : key[0];
    const to = left ? sx : sx + sw;
    const ky = key[1] + key[3] / 2;
    const ly = cell[1] + cell[3] / 2;
    const bend = (to - from) * 0.4;
    return <polyline key={`wire${i}`} points={`${from},${ky} ${from + bend},${ky} ${to - bend / 2},${ly} ${to},${ly}`} />;
  });

  // ------------------------------------------------------------------------- soft keys
  const soft: FixedKey[] = ['soft1', 'soft2', 'soft3', 'soft4'];
  const softKeys = soft.map((k, i) => {
    const f = has(k);
    const [x, y, w, h] = d.soft.first;
    const box: DrawingBox = [x + i * d.soft.step, y, w, h];
    if (!f) return <span key={k} className="arc-phone-key arc-phone-soft" style={at(box)} aria-hidden="true" />;
    const face = fixed(k);
    return touch
      ? setKey(`fixed:${f.index}`, face, 'arc-phone-tkey arc-phone-tsoft', box,
        <span className="arc-phone-clip">{face.label}</span>, { size: text(Math.min(h * TOUCH_SHARE, w * 0.2)) })
      : setKey(`fixed:${f.index}`, face, 'arc-phone-key arc-phone-soft', box, null);
  });
  const softLabels = d.soft_labels && soft.map((k, i) => {
    const f = has(k);
    const [top, height] = d.soft_labels!;
    const box: DrawingBox = [sx + (i * sw) / 4, top, sw / 4, height];
    return (
      <span key={`softlabel${k}`} className="arc-phone-text arc-phone-softlabel" data-first={i === 0}
        data-selected={!!f && selected === `fixed:${f.index}`}
        style={{ ...at(box), fontSize: text(height * 0.62) }} aria-hidden="true">
        <span className="arc-phone-clip">{f ? fixed(k).label : ''}</span>
      </span>
    );
  });

  // -------------------------------------------------------- the ring, and the other keys
  const glyph = (k: FixedKey | DrawnKey) => {
    const name = GLYPH[k];
    return name ? <Icon name={name} style={{ width: '1em', height: '1em', display: 'block' }} /> : null;
  };
  const drawnKey = (k: FixedKey | DrawnKey, box: DrawingBox, className: string, share = 0.55) => {
    const f = has(k as FixedKey);
    const size = text(Math.min(box[2], box[3]) * share, 7);
    if (!f) {
      return (
        <span key={k} className={className} style={{ ...at(box), fontSize: size }} aria-hidden="true">
          {glyph(k)}
        </span>
      );
    }
    const face = fixed(f.key);
    return setKey(`fixed:${f.index}`, face, className, box,
      <>{k === 'ok' ? 'OK' : glyph(k)}{face.assigned && <span className="arc-phone-dot" />}</>,
      { size: k === 'ok' ? text(box[3] * 0.34, 7) : size });
  };
  const ring = d.ring && (() => {
    const [x, y, w, h] = d.ring!;
    return [
      <span key="ring" className="arc-phone-ring" data-silver={d.pad.silver} style={at(d.ring!)} aria-hidden="true" />,
      ...RING.map(([k, cx, cy, share]) =>
        drawnKey(k, [x + (cx - share / 2) * w, y + (cy - share / 2) * h, share * w, share * h],
          `arc-phone-key arc-phone-round${k === 'ok' ? ' arc-phone-ok' : ' arc-phone-arrow'}`, 0.8)),
    ];
  })();
  const others = (Object.entries(d.keys) as [DrawnKey, DrawingBox][]).map(([k, box]) =>
    drawnKey(k, box, `arc-phone-key${k === 'cancel' ? ' arc-phone-round' : ''}`));

  const pad = PAD.map(([digit, letters], i) => {
    const [x, y, w, h] = d.pad.first;
    const box: DrawingBox = [x + (i % 3) * d.pad.step[0], y + Math.floor(i / 3) * d.pad.step[1], w, h];
    return (
      <span key={digit} className="arc-phone-pad" data-silver={d.pad.silver}
        style={{ ...at(box), fontSize: text(h * DIGIT_SHARE) }} aria-hidden="true">
        {digit}{letters && <small>{letters}</small>}
      </span>
    );
  });

  const status = (
    <span className="arc-phone-text arc-phone-status"
      style={{ ...at(d.status), fontSize: text(d.status[3] * 0.7, 7) }} aria-hidden="true">
      <span className="arc-phone-clip">{model}</span>
      {pages > 1 && <span>{page}/{pages}</span>}
    </span>
  );

  return (
    <div className="arc-phone" role="group" aria-label={`${model} keys`} data-colour={layout.colour}
      style={{ aspectRatio: `1000 / ${d.height}`, maxWidth: `${Math.round(d.width_mm * PX_PER_MM)}px` }}>
      {d.body.map((box, i) => <span key={`body${i}`} className="arc-phone-body" style={at(box)} aria-hidden="true" />)}
      {d.trim && <span className="arc-phone-trim" style={at(d.trim)} aria-hidden="true" />}
      <span className="arc-phone-handset" style={at(d.handset)} aria-hidden="true" />
      <span className="arc-phone-pod" style={at(d.pod)} aria-hidden="true" />
      <span className="arc-phone-screen" data-colour={layout.colour} style={at(d.screen)} aria-hidden="true" />
      <svg className="arc-phone-wires" viewBox={`0 0 1000 ${d.height}`} preserveAspectRatio="none" aria-hidden="true">
        {wires}
      </svg>
      {status}
      {cells}
      {softLabels}
      {lineKeys}
      {softKeys}
      {pad}
      {ring}
      {others}
      <span className="arc-phone-volume" style={{ ...at(d.volume), fontSize: text(d.volume[3] * 0.8, 7) }} aria-hidden="true">
        <span>−</span><span>+</span>
      </span>
    </div>
  );
}
